// src/components/Store/foodServiceSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import BaseUrl from '../../../BaseUrl';

// Async Thunks
export const fetchFoodCategories = createAsyncThunk(
  'foodService/fetchFoodCategories',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${BaseUrl.hmsurl || BaseUrl.authurl}FoodCategories/FoodCategoryList`, {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch food categories');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMenus = createAsyncThunk(
  'foodService/fetchMenus',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${BaseUrl.hmsurl || BaseUrl.authurl}Menus`, {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch menus');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMenusByCategory = createAsyncThunk(
  'foodService/fetchMenusByCategory',
  async (categoryId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${BaseUrl.hmsurl || BaseUrl.authurl}Menus/GetMenuByCategoryId/${categoryId}`, {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch menus by category');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createMealInvoice = createAsyncThunk(
  'foodService/createMealInvoice',
  async (mealData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${BaseUrl.hmsurl || BaseUrl.authurl}PatientMeals/PostPatientMeal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(mealData)
      });
      
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || 'Failed to create meal invoice');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPatientMeals = createAsyncThunk(
  'foodService/fetchPatientMeals',
  async (mrn, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const url = mrn 
        ? `${BaseUrl.hmsurl}PatientMeals/GetPatientMealsByMRN/${mrn}`
        : `${BaseUrl.hmsurl}PatientMeals/GetPatientMeals`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch patient meals');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateMealInvoiceStatus = createAsyncThunk(
  'foodService/updateMealInvoiceStatus',
  async ({ id, invoiceStatus }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${BaseUrl.hmsurl}PatientMeals/UpdatePatientMeal/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ invoiceStatus })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update meal invoice status');
      }
      
      return { id, invoiceStatus };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteMealInvoice = createAsyncThunk(
  'foodService/deleteMealInvoice',
  async (id, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${BaseUrl.hmsurl}PatientMeals/DeletePatientMeal/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete meal invoice');
      }
      
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  categories: [],
  menus: [],
  menusByCategory: {},
  patientMeals: [],
  loading: false,
  error: null,
  createLoading: false,
  updateLoading: false
};

const foodServiceSlice = createSlice({
  name: 'foodService',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearMenusByCategory: (state) => {
      state.menusByCategory = {};
    },
    addMealLocally: (state, action) => {
      state.patientMeals.unshift(action.payload);
    },
    updateMealLocally: (state, action) => {
      const index = state.patientMeals.findIndex(meal => meal.id === action.payload.id);
      if (index !== -1) {
        state.patientMeals[index] = { ...state.patientMeals[index], ...action.payload };
      }
    },
    removeMealLocally: (state, action) => {
      state.patientMeals = state.patientMeals.filter(meal => meal.id !== action.payload);
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Categories
      .addCase(fetchFoodCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFoodCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(fetchFoodCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Menus
      .addCase(fetchMenus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMenus.fulfilled, (state, action) => {
        state.loading = false;
        state.menus = action.payload;
      })
      .addCase(fetchMenus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Menus By Category
      .addCase(fetchMenusByCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMenusByCategory.fulfilled, (state, action) => {
        state.loading = false;
        const categoryId = action.meta.arg;
        state.menusByCategory[categoryId] = action.payload;
      })
      .addCase(fetchMenusByCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create Meal Invoice
      .addCase(createMealInvoice.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createMealInvoice.fulfilled, (state, action) => {
        state.createLoading = false;
        if (action.payload) {
          state.patientMeals.unshift(action.payload);
        }
      })
      .addCase(createMealInvoice.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
      })
      
      // Fetch Patient Meals
      .addCase(fetchPatientMeals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientMeals.fulfilled, (state, action) => {
        state.loading = false;
        state.patientMeals = action.payload;
      })
      .addCase(fetchPatientMeals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update Meal Invoice Status
      .addCase(updateMealInvoiceStatus.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateMealInvoiceStatus.fulfilled, (state, action) => {
        state.updateLoading = false;
        const index = state.patientMeals.findIndex(meal => meal.id === action.payload.id);
        if (index !== -1) {
          state.patientMeals[index].invoiceStatus = action.payload.invoiceStatus;
        }
      })
      .addCase(updateMealInvoiceStatus.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      })
      
      // Delete Meal Invoice
      .addCase(deleteMealInvoice.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(deleteMealInvoice.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.patientMeals = state.patientMeals.filter(meal => meal.id !== action.payload);
      })
      .addCase(deleteMealInvoice.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      });
  }
});

export const { 
  clearError, 
  clearMenusByCategory, 
  addMealLocally, 
  updateMealLocally, 
  removeMealLocally 
} = foodServiceSlice.actions;

export default foodServiceSlice.reducer;